import React from 'react';
import { MandantNrPill } from './MandantNrPill';
import { useClientIdentity } from '../hooks/useClientIdentity';
import useAppStore, { useCalculationsSelector } from '../store/appStore';
import { formatMandantNrDisplay, resolveTarifBadgeDisplay } from '../utils/mandantNrFormat';

interface ClientWelcomeHeaderProps {
    subtitle?: string;
    showNotification?: (msg: string, type?: 'success' | 'error') => void;
    userData?: any;
    className?: string;
}

/** Mandant-Nummer; Tarif-Status nur als farbiges Badge. */
export const ClientWelcomeHeader: React.FC<ClientWelcomeHeaderProps> = ({
    subtitle,
    showNotification,
    userData,
    className = '',
}) => {
    const { fullName, mandantNr, godModeClient } = useClientIdentity(userData);
    const clientData = useAppStore((state) => state.clientData);
    const settings = useAppStore((state) => state.settings);
    const team = useAppStore((state) => state.team);
    const setActiveBranch = useAppStore((state) => state.setActiveBranch);
    const calculations = useCalculationsSelector();
    const liveMonatsumsatz = Number(calculations?.monatsumsatzNetto) || 0;

    // Root-Felder (packageTier, customerNumber, billingStatus, …) liegen auf dem
    // Live-Snapshot (userData) / CRM — Store-clientData ist oft nur die nested Map.
    const source = {
        ...(clientData || {}),
        ...(userData || {}),
        ...(godModeClient || {}),
        clientData: {
            ...(clientData || {}),
            ...(userData?.clientData || {}),
            ...(godModeClient?.clientData || {}),
        },
    };

    const customerNumber = String(
        source.customerNumber || source.clientData?.customerNumber || ''
    ).trim();
    const systemNr = formatMandantNrDisplay(mandantNr) || mandantNr || '';
    const displayNr =
        customerNumber && customerNumber !== 'PENDING'
            ? customerNumber
            : customerNumber === 'PENDING'
              ? 'Wartet auf Mandant-Nummer…'
              : systemNr;

    const badge = resolveTarifBadgeDisplay({
        mandantNr: mandantNr || source.mandantNr || source.clientData?.mandantNr,
        client: source,
        settings,
        isLegacy: !!(source.isLegacy || source.clientData?.isLegacy),
        isSolo:
            source.isSolo === true || source.clientData?.isSolo === true
                ? true
                : source.isSolo === false || source.clientData?.isSolo === false
                  ? false
                  : undefined,
        hasEmployeeEval:
            source.hasEmployeeEval === true || source.clientData?.hasEmployeeEval === true
                ? true
                : source.hasEmployeeEval === false || source.clientData?.hasEmployeeEval === false
                  ? false
                  : undefined,
        forceSoloTarif: !!(source.forceSoloTarif || source.clientData?.forceSoloTarif),
        // Live-BWA zuerst — sticky 0/undefined darf Plus nicht blockieren
        nettoMonatsumsatz:
            liveMonatsumsatz > 0
                ? liveMonatsumsatz
                : (Number(source.aggregatedNettoMonatsumsatz || source.clientData?.aggregatedNettoMonatsumsatz) > 0
                    ? Number(source.aggregatedNettoMonatsumsatz || source.clientData?.aggregatedNettoMonatsumsatz)
                    : (Number(source.nettoMonatsumsatz || source.clientData?.nettoMonatsumsatz) > 0
                        ? Number(source.nettoMonatsumsatz || source.clientData?.nettoMonatsumsatz)
                        : null)),
    });

    const hasBranches = !!(clientData?.hasBranches || source.hasBranches || source.clientData?.hasBranches);
    const branches = Array.isArray(clientData?.branches) && clientData.branches.length
        ? clientData.branches
        : (Array.isArray(source.branches) ? source.branches
            : (Array.isArray(source.clientData?.branches) ? source.clientData.branches : []));
    const activeBranchId = String(
        clientData?.activeBranchId
        || source.activeBranchId
        || source.clientData?.activeBranchId
        || branches.find((b: any) => b?.isMain)?.id
        || branches[0]?.id
        || ''
    );

    return (
        <div className={`mb-8 mt-4 ${className}`}>
            <h1 className="text-3xl md:text-4xl font-bold text-pe-text dark:text-white tracking-tight mb-3">
                Hallo <span className="text-pe-accent-text dark:text-[#E6C299] select-text cursor-text">{fullName}</span>!
            </h1>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-3">
                <div className="flex flex-col items-start">
                    <span className="text-[9px] text-pe-muted dark:text-slate-500 font-bold tracking-widest uppercase leading-none mb-1.5">
                        MANDANT-NUMMER
                    </span>
                    {customerNumber === 'PENDING' || displayNr === 'Wartet auf Mandant-Nummer…' ? (
                        <span className="text-sm text-pe-muted font-mono">{displayNr}</span>
                    ) : (
                        <MandantNrPill
                            displayNr={systemNr || displayNr}
                            betreuer={
                                godModeClient?.betreuer ||
                                source.betreuer ||
                                source.clientData?.betreuer ||
                                ''
                            }
                            team={team}
                            tarifLabel={badge?.label}
                            tarifClassName={badge?.className}
                            showNotification={showNotification}
                            size="sm"
                        />
                    )}
                </div>
                {hasBranches && branches.length > 0 && (
                    <div className="flex flex-col items-start mt-1">
                        <span className="text-[9px] text-stone-500 dark:text-slate-500 font-bold tracking-widest uppercase leading-none mb-1">
                            FILIALE
                        </span>
                        <select
                            value={activeBranchId}
                            onChange={(e) => {
                                const next = e.target.value;
                                if (next && next !== activeBranchId && typeof setActiveBranch === 'function') {
                                    void setActiveBranch(next);
                                    if (showNotification) showNotification(`Filiale gewechselt.`, 'success');
                                }
                            }}
                            className="bg-pe-card dark:bg-[#111] border border-pe-border dark:border-[#333] text-pe-text dark:text-white rounded-lg px-3 py-1.5 text-sm font-bold outline-none focus:border-[#E6C299] min-w-[10rem]"
                        >
                            {branches.map((b: any) => (
                                <option key={b.id} value={b.id}>
                                    {b.name || b.id}{b.isMain ? ' (Haupt)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {subtitle && (
                <p className="text-pe-muted dark:text-slate-400 text-lg max-w-3xl">{subtitle}</p>
            )}
        </div>
    );
};
